import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Search as SearchIcon, Error as ErrorIcon, Info as InfoIcon } from '@mui/icons-material';
import installBaseService from '../../services/installBase.service';
import { format } from 'date-fns';

const ServiceRequestsTable = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await installBaseService.getServiceRequests();
        
        // Check if response is an array directly
        if (Array.isArray(response)) {
          setRequests(response);
        }
        // Check if response has items property
        else if (response && response.items) {
          setRequests(response.items);
        }
        // Check if response is an object with data
        else if (response && response.data) {
          setRequests(Array.isArray(response.data) ? response.data : []);
        }
        else {
          setRequests([]);
        }
      } catch (err) {
        setError('Failed to fetch service requests');
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, []);

  const filteredRequests = requests.filter(request => {
    const matchesSearch =
      String(request.ticket_no || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(request.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(request.location_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(request.asset_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPriority = !priorityFilter || 
      String(request.priority || '').toLowerCase() === String(priorityFilter).toLowerCase();
    
    return matchesSearch && matchesPriority;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" icon={<ErrorIcon />} sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Service Requests
      </Typography>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search requests..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            value={priorityFilter}
            label="Priority"
            onChange={e => setPriorityFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ticket No</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Request Type</TableCell>
              <TableCell>Service Category</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Asset ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.length > 0 ? (
              filteredRequests.map(request => (
                <TableRow key={request.id}>
                  <TableCell>{request.ticket_no || '-'}</TableCell>
                  <TableCell>{request.description || '-'}</TableCell>
                  <TableCell>{request.request_type || '-'}</TableCell>
                  <TableCell>{request.service_category || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={request.priority || 'UNKNOWN'}
                      color={
                        String(request.priority || '').toLowerCase() === 'high'
                          ? 'error'
                          : String(request.priority || '').toLowerCase() === 'medium'
                          ? 'warning'
                          : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status || 'UNKNOWN'}
                      color={String(request.status || '').toLowerCase() === 'pending' ? 'error' : 'warning'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {request.request_date ? format(new Date(request.request_date), 'MMM d, yyyy HH:mm') : 'N/A'}
                  </TableCell>
                  <TableCell>{request.location_name || '-'}</TableCell>
                  <TableCell>{request.asset_id || '-'}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No matching requests found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ServiceRequestsTable; 